import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { searchSymbols } from '../../services/SymbolsService';
import {
    analyzeProfitMonitor,
    deleteProfitMonitor,
    getBuySignals,
    getProfitMonitors,
    startProfitMonitor,
    stopProfitMonitor
} from '../../services/ProfitMonitorsService';
import ProfitGoalBar from './ProfitGoalBar';
import { getSettings } from '../../services/SettingsService';
import { profitModeHelp, profitModeShort } from '../../utils/profitProtect';
import './ProfitMonitor.css';

const QUICK_SYMBOLS = ['BTCUSDC', 'ETHUSDC', 'SOLUSDC', 'BNBUSDC', 'XRPUSDC', 'ADAUSDC'];
const INTERVALS = [
    { value: '1m', label: '1 minuto' },
    { value: '5m', label: '5 minutos' },
    { value: '15m', label: '15 minutos' },
    { value: '1h', label: '1 hora' }
];

const STATUS_LABELS = {
    WATCHING: 'Aguardando compra',
    IN_POSITION: 'Em posição',
    STOPPED: 'Parado'
};

function statusLabel(monitor) {
    if (monitor.lastError) return monitor.lastError;
    return STATUS_LABELS[monitor.status] || monitor.status;
}

function statusClass(monitor) {
    if (monitor.lastError) return 'ERROR';
    return monitor.status;
}

function formatPrice(value) {
    const number = parseFloat(value || 0);
    if (!number) return '--';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: number >= 1 ? 4 : 8
    });
}

function formatCompactUsd(value) {
    const number = Number(value);
    if (!number) return '--';
    if (number >= 1e12) return `US$ ${(number / 1e12).toFixed(2)} tri`;
    if (number >= 1e9) return `US$ ${(number / 1e9).toFixed(2)} bi`;
    if (number >= 1e6) return `US$ ${(number / 1e6).toFixed(1)} mi`;
    return `US$ ${number.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

function formatQuote(value, quote = 'USDC') {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: Math.abs(number) >= 1 ? 2 : 6
    });
    if (quote === 'USDC' || quote === 'USDT') return `US$ ${formatted}`;
    return `${formatted} ${quote}`;
}

function signedQuote(value, quote = 'USDC') {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const prefix = number > 0 ? '+' : '';
    return `${prefix}${formatQuote(number, quote)}`;
}

function numeric(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function withPositionValues(monitor) {
    if (!monitor || monitor.status !== 'IN_POSITION') return monitor;
    let buyPrice = numeric(monitor.buyPrice);
    const currentPrice = numeric(monitor.currentPrice);
    const targetProfit = numeric(monitor.targetProfit) || 0;
    const stopLoss = numeric(monitor.stopLoss) || 0;
    const profitPerc = numeric(monitor.profitPerc);
    let quantity = numeric(monitor.quantity);
    let entryValue = numeric(monitor.entryValue);

    if (!buyPrice && currentPrice && profitPerc != null && profitPerc > -100)
        buyPrice = currentPrice / (1 + profitPerc / 100);
    if (!quantity && entryValue && buyPrice)
        quantity = entryValue / buyPrice;
    if (!entryValue && quantity && buyPrice)
        entryValue = quantity * buyPrice;
    if (!buyPrice && quantity && entryValue)
        buyPrice = entryValue / quantity;

    const targetPrice = numeric(monitor.targetPrice) || (buyPrice ? buyPrice * (1 + targetProfit / 100) : null);
    const stopPrice = numeric(monitor.stopPrice) || (buyPrice ? buyPrice * (1 - stopLoss / 100) : null);
    const targetValue = numeric(monitor.targetValue)
        || (quantity && targetPrice ? quantity * targetPrice : (entryValue ? entryValue * (1 + targetProfit / 100) : null));
    const stopValue = numeric(monitor.stopValue)
        || (quantity && stopPrice ? quantity * stopPrice : (entryValue ? entryValue * (1 - stopLoss / 100) : null));
    const currentValue = numeric(monitor.currentValue)
        || (quantity && currentPrice ? quantity * currentPrice : null);
    const profitValue = numeric(monitor.profitValue)
        || (currentValue != null && entryValue != null ? currentValue - entryValue : null);
    const targetPnl = numeric(monitor.targetPnl)
        || (targetValue != null && entryValue != null ? targetValue - entryValue : null);
    const stopPnl = numeric(monitor.stopPnl)
        || (stopValue != null && entryValue != null ? stopValue - entryValue : null);

    return {
        ...monitor,
        buyPrice,
        currentPrice,
        quantity,
        entryValue,
        targetPrice,
        stopPrice,
        targetValue,
        stopValue,
        currentValue,
        profitValue,
        targetPnl,
        stopPnl
    };
}

function apiError(err) {
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    if (data?.message) return data.message;
    return err?.message || 'Erro inesperado.';
}

function toSymbolOptions(rows = []) {
    return rows
        .map(s => s.symbol)
        .filter(symbol => /USDC$/.test(symbol))
        .map(symbol => ({ value: symbol, label: symbol }));
}

function ProfitMonitor() {
    const navigate = useNavigate();
    const [selectedSymbols, setSelectedSymbols] = useState([]);
    const [interval, setIntervalValue] = useState('15m');
    const [percentage, setPercentage] = useState(100);
    const [maxCapital, setMaxCapital] = useState(50);
    const [stopLoss, setStopLoss] = useState(2);
    const [analysis, setAnalysis] = useState(null);
    const [monitors, setMonitors] = useState([]);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const [starting, setStarting] = useState(false);
    const [stoppingId, setStoppingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [buyOpportunities, setBuyOpportunities] = useState({ rows: [], ranking: 'marketCap', scanned: 0 });
    const [loadingOpportunities, setLoadingOpportunities] = useState(false);
    const [profitMode, setProfitMode] = useState('classic');

    const selectedSymbolValues = useMemo(
        () => selectedSymbols.map(item => item.value).filter(Boolean),
        [selectedSymbols]
    );
    const primarySymbol = selectedSymbols[0]?.value || '';
    const activeSymbols = useMemo(
        () => new Set(monitors.filter(m => m.status !== 'STOPPED').map(m => m.symbol)),
        [monitors]
    );

    async function loadSymbolOptions(inputValue) {
        const typed = (inputValue || '').trim().toUpperCase();
        // Backend faz LIKE só com < 6 chars; com >= 6 exige match exato.
        let search = typed;
        if (search.endsWith('USDT'))
            search = `${search.slice(0, -4)}USDC`;
        if (search.length >= 6 && !/USDC$/.test(search))
            search = search.slice(0, 5);
        if (!search)
            search = 'USDC';

        try {
            const data = await searchSymbols(search, false, 1);
            return toSymbolOptions(data.rows || []);
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
            return [];
        }
    }

    useEffect(() => {
        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            loadMonitors();
        };
        tick();
        const timer = setInterval(tick, 30000);
        getSettings()
            .then(result => setProfitMode(result?.predadorProfitMode || 'classic'))
            .catch(() => { /* mantém classic */ });
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function loadOpportunities() {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            setLoadingOpportunities(true);
            try {
                const data = await getBuySignals(interval, selectedSymbolValues);
                if (!cancelled)
                    setBuyOpportunities(data || { rows: [], ranking: 'marketCap', scanned: 0 });
            } catch (err) {
                if (!cancelled)
                    console.error(err);
            } finally {
                if (!cancelled) setLoadingOpportunities(false);
            }
        }

        loadOpportunities();
        const timer = setInterval(loadOpportunities, 60000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [interval, selectedSymbolValues]);

    useEffect(() => {
        if (!primarySymbol) {
            setAnalysis(null);
            return undefined;
        }

        let cancelled = false;

        async function loadAnalysis() {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            setLoadingAnalysis(true);
            try {
                const data = await analyzeProfitMonitor(primarySymbol, interval);
                if (!cancelled) setAnalysis(data);
            } catch (err) {
                if (!cancelled) {
                    setAnalysis(null);
                    setNotification({ type: 'error', text: apiError(err) });
                }
            } finally {
                if (!cancelled) setLoadingAnalysis(false);
            }
        }

        loadAnalysis();
        const timer = setInterval(loadAnalysis, 45000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [primarySymbol, interval]);

    async function loadMonitors() {
        try {
            const data = await getProfitMonitors();
            setMonitors(data.rows || []);
        } catch (err) {
            console.error(err);
        }
    }

    function toggleQuickSymbol(symbol) {
        setSelectedSymbols(current => {
            if (current.some(item => item.value === symbol)) {
                return current.filter(item => item.value !== symbol);
            }
            return [...current, { value: symbol, label: symbol }];
        });
    }

    async function startMonitorsFor(symbols) {
        const list = [...new Set((symbols || []).filter(Boolean))];
        if (!list.length) {
            setNotification({ type: 'error', text: 'Selecione uma ou mais moedas para monitorar.' });
            return false;
        }

        setStarting(true);
        const ok = [];
        const failed = [];
        const payloadBase = {
            interval,
            percentage,
            maxCapital,
            targetProfit: 1.2,
            stopLoss
        };

        for (const symbol of list) {
            if (activeSymbols.has(symbol)) {
                failed.push(`${symbol}: já existe monitor ativo`);
                continue;
            }
            try {
                await startProfitMonitor({ ...payloadBase, symbol });
                ok.push(symbol);
            } catch (err) {
                failed.push(`${symbol}: ${apiError(err)}`);
            }
        }

        await loadMonitors();

        if (ok.length && !failed.length) {
            setNotification({
                type: 'success',
                text: `Monitor iniciado para ${ok.join(', ')}. A compra automática ocorre quando o sinal técnico confirmar.`
            });
            setSelectedSymbols(current => current.filter(item => !ok.includes(item.value)));
        } else if (ok.length) {
            setNotification({
                type: 'info',
                text: `Iniciados: ${ok.join(', ')}. Falhas: ${failed.join(' | ')}`
            });
            setSelectedSymbols(current => current.filter(item => !ok.includes(item.value)));
        } else {
            setNotification({ type: 'error', text: failed.join(' | ') || 'Não foi possível iniciar monitores.' });
        }
        setStarting(false);
        return ok.length > 0;
    }

    async function onStart() {
        await startMonitorsFor(selectedSymbols.map(item => item.value));
    }

    async function startOpportunity(symbol) {
        await startMonitorsFor([symbol]);
    }

    function openAsset(symbol, monitorInterval) {
        navigate(`/profit-monitor/${symbol}?interval=${monitorInterval || interval}`);
    }

    async function onStop(id) {
        setStoppingId(id);
        try {
            await stopProfitMonitor(id);
            setNotification({ type: 'success', text: 'Monitor de lucros parado.' });
            await loadMonitors();
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setStoppingId(null);
        }
    }

    async function onDelete(id, symbol) {
        if (!window.confirm(`Excluir o monitor de ${symbol}? Isso remove as automações e o monitor de candles associados.`))
            return;

        setDeletingId(id);
        try {
            await deleteProfitMonitor(id);
            setNotification({ type: 'success', text: `Monitor de ${symbol} excluído.` });
            await loadMonitors();
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setDeletingId(null);
        }
    }

    const signal = analysis?.signal || 'HOLD';
    const liveMonitor = monitors.find(row => row.symbol === primarySymbol && row.status !== 'STOPPED') || null;

    return (
        <>
            <Menu />
            <main className="content">
                <div className="profit-monitor">
                    <div className="profit-monitor-header">
                        <h1>Monitor de Lucros</h1>
                        <p>
                            Escolha uma ou mais moedas. O sistema analisa o mercado, compra no melhor momento
                            e vende para realizar o lucro (ou no stop-loss).
                        </p>
                    </div>

                    <div className="profit-monitor-grid">
                        <section className="profit-card">
                            <h2>Configurar monitoramento</h2>
                            <div className="profit-field">
                                <label>Moedas</label>
                                <AsyncSelect
                                    isMulti
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadSymbolOptions}
                                    value={selectedSymbols}
                                    onChange={value => setSelectedSymbols(value || [])}
                                    placeholder="Digite para buscar… ex.: BTC, ETH, SOL"
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue
                                            ? `Nenhum par encontrado para "${inputValue}"`
                                            : 'Digite o nome da moeda'
                                    }
                                    loadingMessage={() => 'Buscando moedas...'}
                                    classNamePrefix="react-select"
                                    isDisabled={starting}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        input: base => ({ ...base, color: '#e0e0e0' }),
                                        multiValueLabel: base => ({ ...base, color: '#e0e0e0' })
                                    }}
                                />
                                <div className="profit-quick">
                                    {QUICK_SYMBOLS.map(item => (
                                        <button
                                            key={item}
                                            type="button"
                                            className={selectedSymbols.some(s => s.value === item) ? 'active' : ''}
                                            onClick={() => toggleQuickSymbol(item)}
                                            disabled={starting}
                                        >
                                            {item.replace('USDC', '/USDC')}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="profit-field">
                                <label htmlFor="profitInterval">Intervalo de análise</label>
                                <select
                                    id="profitInterval"
                                    className="profit-select"
                                    value={interval}
                                    onChange={event => setIntervalValue(event.target.value)}
                                    disabled={starting}
                                >
                                    {INTERVALS.map(item => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="profit-field">
                                <label>Capital máximo (USDC): <strong>{maxCapital}</strong></label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min="1"
                                    max="1000000"
                                    step="1"
                                    value={maxCapital}
                                    onChange={event => setMaxCapital(Math.max(0, parseFloat(event.target.value) || 0))}
                                    disabled={starting}
                                />
                            </div>

                            <div className="profit-field">
                                <label>
                                    Uso do capital: <strong>{percentage}%</strong>
                                    {' '}→ ~{(maxCapital * percentage / 100).toFixed(2)} por compra
                                </label>
                                <input
                                    type="range"
                                    className="form-range"
                                    min="1"
                                    max="100"
                                    value={percentage}
                                    onChange={event => setPercentage(parseFloat(event.target.value))}
                                    disabled={starting}
                                />
                            </div>

                            <div className="profit-field">
                                <label>Stop loss: <strong>{stopLoss}%</strong></label>
                                <input
                                    type="range"
                                    className="form-range"
                                    min="0.1"
                                    max="20"
                                    step="0.1"
                                    value={stopLoss}
                                    onChange={event => setStopLoss(parseFloat(event.target.value))}
                                    disabled={starting}
                                />
                            </div>

                            <button
                                className="profit-btn"
                                type="button"
                                onClick={onStart}
                                disabled={starting || !selectedSymbols.length}
                            >
                                {starting
                                    ? 'Iniciando...'
                                    : `Iniciar monitor${selectedSymbols.length > 1 ? 'es' : ''} (${selectedSymbols.length || 0})`}
                            </button>
                            <p className="profit-empty" style={{ textAlign: 'left', padding: '12px 0 0' }}>
                                Compra no cruzamento do MACD (linha MACD acima do signal), RSI abaixo de 65 e EMAs de alta. Confirma também a tendência no tempo maior.
                                A % usa o capital máximo informado (ex.: 100% de 50 = 50 USDC), sem ultrapassar o saldo da carteira.
                                {' '}{profitModeHelp(profitMode)}
                            </p>
                        </section>

                        <section className="profit-card">
                            <h2>Análise ao vivo{primarySymbol ? ` · ${primarySymbol}` : ''}</h2>
                            {!primarySymbol && (
                                <div className="profit-empty">
                                    Selecione uma ou mais moedas. A análise ao vivo usa a primeira da lista.
                                </div>
                            )}
                            {primarySymbol && loadingAnalysis && !analysis && (
                                <div className="profit-loading">Analisando {primarySymbol}...</div>
                            )}
                            {analysis && (
                                <>
                                    <div
                                        className={`profit-signal ${signal}${signal === 'BUY' ? ' clickable' : ''}`}
                                        onClick={signal === 'BUY' ? () => openAsset(primarySymbol) : undefined}
                                        role={signal === 'BUY' ? 'link' : undefined}
                                        tabIndex={signal === 'BUY' ? 0 : undefined}
                                        onKeyDown={event => {
                                            if (signal === 'BUY' && (event.key === 'Enter' || event.key === ' ')) {
                                                event.preventDefault();
                                                openAsset(primarySymbol);
                                            }
                                        }}
                                    >
                                        <div>
                                            <div className="profit-signal-label">{analysis.signalLabel}</div>
                                            <small>
                                                {analysis.summary}
                                                {signal === 'BUY' ? ' Clique para ver o ativo e o motivo da tendência.' : ''}
                                            </small>
                                        </div>
                                        <div className="profit-price">
                                            <strong>${formatPrice(analysis.price)}</strong>
                                            <span>
                                                {analysis.changePerc > 0 ? '+' : ''}
                                                {analysis.changePerc ?? '--'}% recente
                                            </span>
                                        </div>
                                    </div>
                                    <div className="profit-indicators">
                                        <div className="profit-indicator">
                                            <span>RSI</span>
                                            <strong>{analysis.indicators?.rsi ?? '--'}</strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>MACD</span>
                                            <strong>
                                                {analysis.indicators?.macdHistogram > 0
                                                    ? 'Alta'
                                                    : analysis.indicators?.macdHistogram < 0
                                                        ? 'Baixa'
                                                        : '--'}
                                            </strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>EMA 20</span>
                                            <strong>{formatPrice(analysis.indicators?.ema20)}</strong>
                                        </div>
                                        <div className="profit-indicator">
                                            <span>EMA 50</span>
                                            <strong>{formatPrice(analysis.indicators?.ema50)}</strong>
                                        </div>
                                    </div>
                                    <ProfitGoalBar analysis={analysis} monitor={liveMonitor} />
                                    <ul className="profit-reasons">
                                        {(analysis.reasons || []).map(reason => (
                                            <li key={reason}>{reason}</li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </section>
                    </div>

                    <section className="profit-opportunities">
                        <div className="profit-card profit-card-opportunities">
                            <div className="profit-opportunities-header">
                                <div>
                                    <h2>Oportunidades de compra</h2>
                                    <p>
                                        Moedas com o mesmo sinal verde da análise ao vivo, entre as 100 de maior volume USDC na Binance
                                        {selectedSymbolValues.length ? ' e as que selecionares' : ''}.
                                        Clique na moeda para ver o ativo, a barra da meta e o motivo da tendência.
                                        {buyOpportunities.scanned
                                            ? ` Analisadas ${buyOpportunities.scanned} no intervalo ${interval}.`
                                            : ''}
                                    </p>
                                </div>
                                {loadingOpportunities && (
                                    <span className="profit-opportunities-status">Atualizando...</span>
                                )}
                            </div>
                            {loadingOpportunities && !(buyOpportunities.rows || []).length && (
                                <div className="profit-loading">Procurando moedas com sinal verde...</div>
                            )}
                            {!loadingOpportunities && !(buyOpportunities.rows || []).length && (
                                <div className="profit-empty">
                                    Nenhuma das principais moedas está com sinal verde de compra agora.
                                </div>
                            )}
                            {(buyOpportunities.rows || []).map(item => (
                                <div
                                    className="profit-opp-row profit-opp-row-clickable"
                                    key={item.symbol}
                                    onClick={() => openAsset(item.symbol)}
                                    role="link"
                                    tabIndex={0}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            openAsset(item.symbol);
                                        }
                                    }}
                                >
                                    <div className="profit-opp-coin">
                                        <strong>{item.symbol.replace('USDC', '/USDC')}</strong>
                                        <span>
                                            {item.marketCapRank ? `#${item.marketCapRank} · ` : ''}
                                            {item.marketCap
                                                ? formatCompactUsd(item.marketCap)
                                                : `Vol. ${formatCompactUsd(item.quoteVolume)}`}
                                            {' · ver detalhes'}
                                        </span>
                                    </div>
                                    <div className="profit-opp-signal">COMPRAR</div>
                                    <div className="profit-opp-metrics">
                                        <span>${formatPrice(item.price)}</span>
                                        <small>
                                            RSI {item.indicators?.rsi ?? '--'}
                                            {item.changePerc != null
                                                ? ` · ${item.changePerc > 0 ? '+' : ''}${item.changePerc}%`
                                                : ''}
                                            {item.automationReady ? ' · pronta para comprar' : ''}
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        className="profit-opp-add"
                                        onClick={event => {
                                            event.stopPropagation();
                                            startOpportunity(item.symbol);
                                        }}
                                        disabled={starting || activeSymbols.has(item.symbol)}
                                    >
                                        {activeSymbols.has(item.symbol)
                                            ? 'Já monitorada'
                                            : starting
                                                ? 'Iniciando...'
                                                : 'Iniciar monitor'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="profit-monitor-list">
                        <div className="profit-card">
                            <h2>Monitores ativos</h2>
                            {monitors.length === 0 && (
                                <div className="profit-empty">Nenhum monitor de lucros criado ainda.</div>
                            )}
                            {monitors.map(item => {
                                const monitor = withPositionValues(item);
                                const inPosition = monitor.status === 'IN_POSITION';
                                const quote = monitor.quoteAsset || 'USDC';
                                const closedCycles = Number(monitor.closedCycles) || 0;
                                const accumulated = monitor.totalProfitValue != null
                                    ? monitor.totalProfitValue
                                    : (inPosition ? monitor.profitValue : null);
                                const accumulatedPerc = monitor.totalProfitPerc != null
                                    ? monitor.totalProfitPerc
                                    : monitor.profitPerc;
                                const currentPnlClass = monitor.profitPerc > 0 ? 'positive' : monitor.profitPerc < 0 ? 'negative' : '';
                                const accumulatedPnlClass = accumulated > 0 ? 'positive' : accumulated < 0 ? 'negative' : '';
                                return (
                                    <div
                                        className="profit-monitor-row profit-monitor-row-clickable"
                                        key={monitor.id}
                                        onClick={() => openAsset(monitor.symbol, monitor.interval)}
                                        role="link"
                                        tabIndex={0}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                openAsset(monitor.symbol, monitor.interval);
                                            }
                                        }}
                                    >
                                        <div>
                                            <strong>{monitor.symbol}</strong>
                                            {monitor.isPredador
                                                ? <span className="badge bg-warning text-dark ms-2">{monitor.buyOnly ? 'Só compra' : 'Trade automático'}</span>
                                                : null}
                                            <div>
                                                {monitor.interval}
                                                {monitor.maxCapital != null
                                                    ? ` · ${monitor.percentage}% de ${monitor.maxCapital} ${quote}`
                                                    : ` · ${monitor.percentage}%`}
                                                {monitor.buyOnly
                                                    ? ' · só compra'
                                                    : ` · ${profitModeShort(monitor.profitProfile || monitor.profitMode || profitMode)} · stop ${monitor.stopLoss}%`}
                                            </div>
                                            <div className="profit-row-hint">Clique para ver a posição e o resultado acumulado</div>
                                        </div>
                                        <div>
                                            <button
                                                type="button"
                                                className={`profit-badge profit-status-btn ${statusClass(monitor)}`}
                                                title={monitor.lastError || statusLabel(monitor)}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    if (monitor.lastError)
                                                        setNotification({ type: 'error', text: `${monitor.symbol}: ${monitor.lastError}` });
                                                }}
                                            >
                                                {statusLabel(monitor)}
                                            </button>
                                        </div>
                                        <div>
                                            <div className={`profit-pnl ${inPosition ? currentPnlClass : accumulatedPnlClass}`}>
                                                {inPosition ? (
                                                    <>
                                                        {monitor.profitPerc > 0 ? '+' : ''}{monitor.profitPerc ?? '--'}%
                                                        {monitor.profitValue != null ? ` (${signedQuote(monitor.profitValue, quote)})` : ''}
                                                        <div>pagou {formatQuote(monitor.entryValue, quote)}</div>
                                                        {monitor.buyOnly
                                                            ? <div className="profit-pnl-exits">sem venda automática</div>
                                                            : (
                                                                <div className="profit-pnl-exits">
                                                                    vende a ${formatPrice(monitor.targetPrice)} ({formatQuote(monitor.targetValue, quote)}) · stop ${formatPrice(monitor.stopPrice)}
                                                                </div>
                                                            )}
                                                    </>
                                                ) : (
                                                    <span>Preço atual: {formatPrice(monitor.currentPrice)}</span>
                                                )}
                                                {(closedCycles > 0 || accumulated != null) && (
                                                    <div className={`profit-pnl-accumulated ${accumulatedPnlClass}`}>
                                                        Acumulado {accumulatedPerc > 0 ? '+' : ''}{accumulatedPerc ?? '--'}%
                                                        {accumulated != null ? ` · ${signedQuote(accumulated, quote)}` : ''}
                                                        {closedCycles ? ` · ${closedCycles} ciclo${closedCycles === 1 ? '' : 's'}` : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="profit-actions">
                                            {monitor.status !== 'STOPPED' && (
                                                <button
                                                    className="profit-stop"
                                                    type="button"
                                                    disabled={stoppingId === monitor.id || deletingId === monitor.id}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        onStop(monitor.id);
                                                    }}
                                                >
                                                    {stoppingId === monitor.id ? 'Parando...' : 'Parar'}
                                                </button>
                                            )}
                                            <button
                                                className="profit-delete"
                                                type="button"
                                                disabled={deletingId === monitor.id || stoppingId === monitor.id}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    onDelete(monitor.id, monitor.symbol);
                                                }}
                                            >
                                                {deletingId === monitor.id ? 'Excluindo...' : 'Excluir'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>
            </main>
            <Footer />
            {notification.text && (
                <Toast
                    type={notification.type}
                    text={notification.text}
                    onClose={() => setNotification({ type: '', text: '' })}
                />
            )}
        </>
    );
}

export default ProfitMonitor;
